import { useState, useEffect } from "react";
import { Drawer } from "../../shared";
import {
  FaCheck,
  FaTimes,
  FaShieldAlt,
  FaKey,
  FaPlus,
  FaTabletAlt,
  FaSyncAlt,
  FaExclamationTriangle,
  FaArrowRight,
  FaClock,
  FaCheckCircle,
  FaEye,
  FaEyeSlash,
  FaCopy,
} from "react-icons/fa";
import {
  useApproveBootstrapRequestMutation,
  useRejectBootstrapRequestMutation,
  useApproveMigrationMutation,
  useRejectPendingDeviceMutation,
} from "../../../store";
import { useRevealBootstrapCredentialsMutation } from "../../../store/storageRoles/pendingBootstrapApi";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import { useAlert } from "../../../context/AlertContext";
import { buildSteps, derivePhase } from "./PendingClientCard";
import useDrawerTransition from "../../../Hooks/useDrawerTransition";

const MODE_META = {
  new_install: {
    label: "Yeni Kurulum",
    Icon: FaPlus,
    classes: "bg-blue-50 text-blue-700 border-blue-200",
    desc:
      "Cihaz fabrika ayarında — yeni seri no atanacak, makine adı ve göz sayısı seçilecek.",
  },
  screen_replacement: {
    label: "Ekran Değişimi",
    Icon: FaTabletAlt,
    classes: "bg-amber-50 text-amber-700 border-amber-200",
    desc: "Mevcut bir cihazın ekranı değiştiriliyor — hedef cihaz seçilecek.",
  },
  migration: {
    label: "Yükseltme",
    Icon: FaSyncAlt,
    classes: "bg-emerald-50 text-emerald-700 border-emerald-200",
    desc:
      "Eski APK upgrade — cihazın bağlantı şifresi yenilenip cihaza yazılacak.",
  },
};

const BOOTSTRAP_STATUS_LABEL = {
  pending_approval: "Onay Bekliyor",
  approved: "Onaylandı",
  credentials_issued: "Şifre Verildi",
  rejected: "Reddedildi",
};

const PENDING_STATUS_LABEL = {
  pending: "Onay Bekliyor",
  claimed: "İşleniyor",
  assigned: "Atandı",
  rejected: "Reddedildi",
};

const STEP_VISUAL = {
  done: { bg: "bg-emerald-500", text: "text-emerald-700", Icon: FaCheck },
  current: { bg: "bg-amber-500", text: "text-amber-700", Icon: FaClock },
  pending: { bg: "bg-gray-300", text: "text-gray-500", Icon: FaClock },
  rejected: { bg: "bg-red-500", text: "text-red-700", Icon: FaTimes },
  skipped: { bg: "bg-gray-200", text: "text-gray-400", Icon: FaCheck },
};

function fmtDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function InfoRow({ label, value, mono }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-3 text-xs py-1">
      <span className="text-mainSecond/60 shrink-0">{label}</span>
      <span
        className={`text-right text-mainSecond ${
          mono ? "font-mono break-all" : ""
        }`}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Birlestirilmis bekleyen istemci drawer'i.
 * Mevcut faza gore aksiyon paneli render eder:
 *   - bootstrap_pending  → Onayla / Reddet butonlari (inline)
 *   - awaiting_mode      → bilgi kutucugu (cihazin mod secmesi bekleniyor)
 *   - mode_pending + new_install/screen_replacement → "Forma Git" butonu (page handler)
 *   - mode_pending + migration → inline Onayla
 *   - mode_claimed/_pending → Reddet butonu
 *   - completed/rejected → readonly
 */
export default function PendingClientDrawer({
  open,
  client: clientProp,
  onClose,
  onSuccess,
  onOpenForm,
}) {
  // Kapanış animasyonu boyunca istemci kaydı dondurulur (çağıran taraf
  // kapatırken client'ı null'a çekiyor).
  const { mounted, data: client } = useDrawerTransition(open, clientProp);
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { showSuccess, showError } = useAlert();
  const { hasPermission } = usePermission();
  const canManage = hasPermission("pendingDevices.manage");

  const [approveBootstrap, { isLoading: isApprovingBs }] =
    useApproveBootstrapRequestMutation();
  const [rejectBootstrap, { isLoading: isRejectingBs }] =
    useRejectBootstrapRequestMutation();
  const [approveMigration, { isLoading: isMigrating }] =
    useApproveMigrationMutation();
  const [rejectPending, { isLoading: isRejectingPending }] =
    useRejectPendingDeviceMutation();
  const [revealCredentials, { isLoading: isRevealing }] =
    useRevealBootstrapCredentialsMutation();

  const [showRejectBootstrap, setShowRejectBootstrap] = useState(false);
  const [bootstrapReason, setBootstrapReason] = useState("");
  const [showRejectPending, setShowRejectPending] = useState(false);
  const [pendingReason, setPendingReason] = useState("");
  const [revealedCreds, setRevealedCreds] = useState(null);
  const [revealError, setRevealError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState("");
  // Admin override: cihaz pending.mode "new_install" gondermis olsa bile
  // admin "mevcut cihaza ata" diyebilir veya tersi. null = cihaz tercihi
  // (varsayilan). Server PR #95 ile iki endpoint iki modu da kabul ediyor.
  const [overrideMode, setOverrideMode] = useState(null);

  useEffect(() => {
    if (!open) {
      setShowRejectBootstrap(false);
      setBootstrapReason("");
      setShowRejectPending(false);
      setPendingReason("");
      setRevealedCreds(null);
      setRevealError("");
      setShowPassword(false);
      setCopiedField("");
      setOverrideMode(null);
    }
  }, [open]);

  // Bootstrap kaydi degisirse (drawer aciktayken yeni client'a gectiyse)
  // gosterilen sifreyi temizle.
  useEffect(() => {
    setRevealedCreds(null);
    setRevealError("");
    setShowPassword(false);
    setCopiedField("");
  }, [client?.bootstrap?._id]);

  if (!mounted || !client) return null;

  const { tempClientId, bootstrap, pending } = client;
  const phase = derivePhase({ bootstrap, pending });
  const steps = buildSteps({ bootstrap, pending });
  const modeMeta = pending?.mode ? MODE_META[pending.mode] : null;
  const ModeIcon = modeMeta?.Icon;
  // Doğrulama kodu — install (pending) varsa ondan, yoksa bootstrap'tan.
  const verificationCode = pending?.code || bootstrap?.code || null;

  const handleApproveBootstrap = async () => {
    if (!bootstrap?._id) return;
    try {
      await approveBootstrap({ token, id: bootstrap._id }).unwrap();
      showSuccess(`Ön kayıt onaylandı: ${tempClientId.slice(0, 12)}…`);
      onSuccess?.();
    } catch (err) {
      showError(err?.data?.message || "Ön kayıt onaylanamadı");
    }
  };

  const handleRejectBootstrap = async () => {
    if (!bootstrap?._id) return;
    try {
      await rejectBootstrap({
        token,
        id: bootstrap._id,
        reason: bootstrapReason,
      }).unwrap();
      showSuccess(`Ön kayıt reddedildi: ${tempClientId.slice(0, 12)}…`);
      onSuccess?.();
      onClose();
    } catch (err) {
      showError(err?.data?.message || "Reddedilemedi");
    }
  };

  const handleApproveMigration = async () => {
    if (!pending?._id) return;
    try {
      await approveMigration({ token, id: pending._id }).unwrap();
      showSuccess(`Yükseltme onaylandı: ${pending.claimedSerialNo || ""}`);
      onSuccess?.();
      onClose();
    } catch (err) {
      showError(err?.data?.message || "Yükseltme onaylanamadı");
    }
  };

  const handleRejectPending = async () => {
    if (!pending?._id) return;
    try {
      await rejectPending({
        token,
        id: pending._id,
        reason: pendingReason,
      }).unwrap();
      showSuccess("İstek reddedildi");
      onSuccess?.();
      onClose();
    } catch (err) {
      showError(err?.data?.message || "Reddedilemedi");
    }
  };

  const handleRevealCredentials = async () => {
    if (!bootstrap?._id) return;
    setRevealError("");
    setRevealedCreds(null);
    setShowPassword(false);
    setCopiedField("");
    try {
      const data = await revealCredentials({
        token,
        id: bootstrap._id,
      }).unwrap();
      setRevealedCreds(data);
    } catch (err) {
      setRevealError(err?.data?.message || err?.error || "Şifre alınamadı.");
    }
  };

  const handleCopy = async (field, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(""), 1800);
    } catch (_) {}
  };

  const renderActionPanel = () => {
    // pendingDevices.manage yetkisi olmayan kullanıcı sadece görüntüler;
    // aksiyon gerektiren fazlarda buton yerine bilgilendirme gösterilir.
    const ACTIONABLE_PHASES = [
      "bootstrap_pending",
      "mode_pending",
      "mode_claimed",
    ];
    if (!canManage && ACTIONABLE_PHASES.includes(phase)) {
      return (
        <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg flex items-start gap-2">
          <FaEye className="w-3.5 h-3.5 text-gray-500 mt-0.5 shrink-0" />
          <p className="text-xs text-gray-600">
            Bu isteği yalnızca görüntüleyebilirsiniz. Onaylama, reddetme veya
            atama işlemleri için yönetim yetkisi gereklidir.
          </p>
        </div>
      );
    }
    if (phase === "bootstrap_pending") {
      if (showRejectBootstrap) {
        return (
          <div className="space-y-3">
            <label className="block text-xs font-medium text-gray-700">
              Reddetme sebebi (opsiyonel)
            </label>
            <textarea
              value={bootstrapReason}
              onChange={(e) => setBootstrapReason(e.target.value)}
              rows={3}
              maxLength={200}
              placeholder="Örn. tanınmayan cihaz / abuse şüphesi"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowRejectBootstrap(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-lg text-sm font-medium hover:bg-gray-50"
                disabled={isRejectingBs}
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={handleRejectBootstrap}
                disabled={isRejectingBs}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-lg text-sm font-medium shadow-sm disabled:opacity-60"
              >
                {isRejectingBs ? "Reddediliyor..." : "Reddet"}
              </button>
            </div>
          </div>
        );
      }
      return (
        <div className="space-y-3">
          <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-lg">
            <FaExclamationTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800">
              Bu sadece bağlantı (sunucu+MQTT) onayıdır. Cihaza kalıcı seri no
              atamak için ardından kurulum onayı verilmeli.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowRejectBootstrap(true)}
              className="flex-1 px-4 py-2 border border-fifth/30 text-fifth bg-white rounded-lg text-sm font-medium hover:bg-fifth/5"
            >
              Reddet
            </button>
            <button
              type="button"
              onClick={handleApproveBootstrap}
              disabled={isApprovingBs}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg text-sm font-semibold shadow-sm disabled:opacity-60"
            >
              {isApprovingBs ? "Onaylanıyor..." : "Bağlantıyı Onayla"}
            </button>
          </div>
        </div>
      );
    }

    if (phase === "bootstrap_rejected") {
      return (
        <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg">
          <div className="flex items-start gap-2">
            <FaTimes className="w-3.5 h-3.5 text-red-600 mt-0.5 shrink-0" />
            <div className="text-xs text-red-800">
              <div className="font-semibold mb-0.5">Bağlantı reddedildi</div>
              {bootstrap?.rejectedReason && (
                <div className="text-red-700">{bootstrap.rejectedReason}</div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (phase === "awaiting_mode") {
      return (
        <div className="px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-lg">
          <div className="flex items-start gap-2">
            <FaClock className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0" />
            <p className="text-xs text-blue-800">
              Cihaz bağlantı şifresini aldı. Şu an kullanıcının makinede bir mod
              (Yeni Kurulum / Ekran Değişimi / Yükseltme) seçmesi bekleniyor.
            </p>
          </div>
        </div>
      );
    }

    if (
      phase === "mode_pending" ||
      phase === "mode_claimed"
    ) {
      if (showRejectPending) {
        return (
          <div className="space-y-3">
            <label className="block text-xs font-medium text-gray-700">
              Reddetme sebebi (opsiyonel)
            </label>
            <textarea
              value={pendingReason}
              onChange={(e) => setPendingReason(e.target.value)}
              rows={3}
              maxLength={200}
              placeholder="Örn. yetkisiz cihaz / hatalı tcid"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowRejectPending(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-lg text-sm font-medium hover:bg-gray-50"
                disabled={isRejectingPending}
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={handleRejectPending}
                disabled={isRejectingPending}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-lg text-sm font-medium shadow-sm disabled:opacity-60"
              >
                {isRejectingPending ? "Reddediliyor..." : "Reddet"}
              </button>
            </div>
          </div>
        );
      }

      // Migration → inline approve
      if (pending?.mode === "migration") {
        return (
          <div className="space-y-3">
            <div className="px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-lg flex items-start gap-2">
              <FaExclamationTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800">
                Onayladığında cihazın bağlantı şifresi yenilenir, yeni bilgiler
                cihaza yazılır ve cihaz yeniden bağlanır.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowRejectPending(true)}
                className="flex-1 px-4 py-2 border border-fifth/30 text-fifth bg-white rounded-lg text-sm font-medium hover:bg-fifth/5"
              >
                Reddet
              </button>
              <button
                type="button"
                onClick={handleApproveMigration}
                disabled={isMigrating}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg text-sm font-semibold shadow-sm disabled:opacity-60"
              >
                {isMigrating ? "Onaylanıyor..." : "Yükseltmeyi Onayla"}
              </button>
            </div>
          </div>
        );
      }

      // new_install / screen_replacement → page acar. Admin override:
      // overrideMode null ise cihazin tercihi (pending.mode) kullanilir,
      // degilse admin'in radio secimi.
      const effectiveMode = overrideMode || pending?.mode;
      const formButtonLabel =
        effectiveMode === "screen_replacement"
          ? "Ekran Değişimi Formuna Git"
          : "Yeni Kurulum Formuna Git";
      const isOverriding =
        overrideMode !== null && overrideMode !== pending?.mode;

      return (
        <div className="space-y-3">
          {modeMeta && (
            <div className="px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-lg flex items-start gap-2">
              {ModeIcon && (
                <ModeIcon className="w-3.5 h-3.5 text-gray-600 mt-0.5 shrink-0" />
              )}
              <p className="text-xs text-gray-700">{modeMeta.desc}</p>
            </div>
          )}

          {/* Onay tipi radio seçim — admin cihazın gönderdiği mod'u
              override edebilir. Cihaz mode'unu varsayılan göster. */}
          <div className="px-3 py-2.5 bg-white border border-gray-200 rounded-lg space-y-2">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              Onay Tipi
            </div>
            <label className="flex items-start gap-2 cursor-pointer group">
              <input
                type="radio"
                name="approve-mode"
                checked={effectiveMode === "new_install"}
                onChange={() => setOverrideMode("new_install")}
                className="mt-0.5 cursor-pointer accent-main"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-800 group-hover:text-main">
                  Yeni Cihaz Olarak Oluştur
                  {pending?.mode === "new_install" && (
                    <span className="ml-2 text-[10px] font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                      Cihaz Tercihi
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-gray-500">
                  Yeni seri no atanır, makine adı + göz sayısı seçilir.
                </div>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer group">
              <input
                type="radio"
                name="approve-mode"
                checked={effectiveMode === "screen_replacement"}
                onChange={() => setOverrideMode("screen_replacement")}
                className="mt-0.5 cursor-pointer accent-main"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-800 group-hover:text-main">
                  Mevcut Bir Cihaza Ata
                  {pending?.mode === "screen_replacement" && (
                    <span className="ml-2 text-[10px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                      Cihaz Tercihi
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-gray-500">
                  Hedef cihazı seçeceksiniz, mevcut seri no korunur.
                </div>
              </div>
            </label>
            {isOverriding && (
              <div className="px-2 py-1.5 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800 flex items-start gap-1.5">
                <FaExclamationTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                Cihazın tercihinden farklı bir mod seçtiniz — denetim için
                audit log'a yazılacak.
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowRejectPending(true)}
              className="flex-1 px-4 py-2 border border-fifth/30 text-fifth bg-white rounded-lg text-sm font-medium hover:bg-fifth/5"
            >
              Reddet
            </button>
            <button
              type="button"
              onClick={() => onOpenForm?.({ ...pending, mode: effectiveMode })}
              className="flex-[2] px-4 py-2 bg-main text-new rounded-lg text-sm font-semibold hover:bg-mainSecond flex items-center justify-center gap-2"
            >
              {formButtonLabel}
              <FaArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      );
    }

    if (phase === "mode_rejected") {
      return (
        <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg flex items-start gap-2">
          <FaTimes className="w-3.5 h-3.5 text-red-600 mt-0.5 shrink-0" />
          <div className="text-xs text-red-800">
            <div className="font-semibold mb-0.5">Mod isteği reddedildi</div>
            {pending?.rejectedReason && (
              <div className="text-red-700">{pending.rejectedReason}</div>
            )}
          </div>
        </div>
      );
    }

    if (phase === "completed") {
      return (
        <div className="px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg flex items-start gap-2">
          <FaCheckCircle className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-xs text-emerald-800">
            <div className="font-semibold mb-0.5">Tamamlandı</div>
            {pending?.assignedSerialNo && (
              <div>
                Seri No: <span className="font-mono">{pending.assignedSerialNo}</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      position="right"
      width="md"
      title="İstek Detayı"
    >
      <Drawer.Body className="px-5 py-4 space-y-5">
        {/* Hero — doğrulama kodu (pending varsa ondan, yoksa bootstrap'tan),
            mod rozeti, client id. Kod yoksa "—" placeholder ile gosterilir
            (kullanici talebi: kart/box yukseklik tutarli). */}
        <div className="bg-input/30 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-wider text-mainSecond/60">
            Doğrulama Kodu
          </div>
          <div
            className={`font-mono font-bold text-3xl tracking-widest mb-2 ${
              verificationCode ? "text-main" : "text-mainSecond/30"
            }`}
          >
            {verificationCode || "——————"}
          </div>
          {modeMeta && (
            <div
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${modeMeta.classes}`}
            >
              {ModeIcon && <ModeIcon className="w-3 h-3" />}
              {modeMeta.label}
            </div>
          )}
          <div className="text-[10px] uppercase tracking-wider text-mainSecond/60 mt-3">
            Client ID
          </div>
          <div className="text-xs text-mainSecond/70 break-all font-mono">
            {tempClientId}
          </div>
        </div>

        {/* Timeline */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-mainSecond/60 mb-2 font-semibold">
            İlerleme
          </div>
          <ol className="space-y-3">
            {steps.map((step, idx) => {
              const visual = STEP_VISUAL[step.state] || STEP_VISUAL.pending;
              const StepIcon = visual.Icon;
              const isLast = idx === steps.length - 1;
              return (
                <li key={step.id} className="flex items-start gap-3 relative">
                  <div className="flex flex-col items-center shrink-0">
                    <div
                      className={`w-8 h-8 rounded-full ${visual.bg} flex items-center justify-center text-white shadow-sm`}
                    >
                      <StepIcon className="w-3 h-3" />
                    </div>
                    {!isLast && (
                      <div className="w-0.5 grow bg-gray-200 mt-1" style={{ minHeight: 16 }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 pb-2">
                    <div className={`text-sm font-semibold ${visual.text}`}>
                      {step.label}
                    </div>
                    {step.detail && (
                      <div className="text-xs text-mainSecond/60 mt-0.5">
                        {step.detail}
                      </div>
                    )}
                    {step.ts && (
                      <div className="text-[11px] text-mainSecond/50 mt-0.5">
                        {fmtDate(step.ts)}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Bootstrap Details */}
        {bootstrap && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-mainSecond/60 mb-1.5 font-semibold flex items-center gap-1.5">
              <FaShieldAlt className="w-3 h-3" />
              Bağlantı Bilgisi
            </div>
            <div className="rounded-lg border border-input bg-white px-3 py-2 divide-y divide-input/60">
              <InfoRow
                label="Durum"
                value={BOOTSTRAP_STATUS_LABEL[bootstrap.status] || bootstrap.status}
              />
              <InfoRow
                label="Onay Tipi"
                value={
                  bootstrap.approvalType === "auto_migration"
                    ? "Otomatik (Yükseltme)"
                    : "Manuel"
                }
              />
              <InfoRow label="Seri No" value={bootstrap.serialNo} mono />
              <InfoRow label="Cihaz" value={bootstrap.deviceModel} />
              <InfoRow label="Uygulama Sürümü" value={bootstrap.appVersion} />
              <InfoRow label="IP Adresi" value={bootstrap.ipAddress} mono />
              <InfoRow label="Donanım ID" value={bootstrap.hardwareId} mono />
              <InfoRow
                label="MQTT Kullanıcı"
                value={bootstrap.emqxUsername}
                mono
              />
            </div>

            {/* Kurulum sifresi (bootstrapPassword) — yalnizca credentials_issued
                ve pendingDevices.manage yetkisi olanlara gosterilir */}
            {canManage && bootstrap.status === "credentials_issued" && (
              <div className="mt-3">
                {!revealedCreds && !isRevealing && !revealError && (
                  <button
                    type="button"
                    onClick={handleRevealCredentials}
                    className="w-full px-3 py-2 rounded-lg border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 font-medium text-sm transition flex items-center justify-center gap-2"
                  >
                    <FaKey className="text-emerald-500" />
                    Kurulum Şifresini Göster
                  </button>
                )}

                {isRevealing && (
                  <div className="text-xs text-mainSecond/60 px-3 py-2">
                    Yükleniyor...
                  </div>
                )}

                {!isRevealing && revealError && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2 flex items-center justify-between gap-2">
                    <span>{revealError}</span>
                    <button
                      type="button"
                      onClick={handleRevealCredentials}
                      className="text-red-700 underline"
                    >
                      Tekrar Dene
                    </button>
                  </div>
                )}

                {!isRevealing && revealedCreds?.bootstrapPassword && (
                  <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-3 space-y-2">
                    {revealedCreds.serialNo && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-mainSecond/60 mb-0.5">
                          Seri No
                        </div>
                        <code className="font-mono text-sm break-all text-main">
                          {revealedCreds.serialNo}
                        </code>
                      </div>
                    )}
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-mainSecond/60 mb-1">
                        Kurulum Şifresi (6 haneli)
                      </div>
                      <div className="flex items-center gap-2 bg-white border border-input rounded-lg px-3 py-2">
                        <code className="flex-1 font-mono text-2xl tracking-[0.4em] font-bold text-main">
                          {showPassword
                            ? revealedCreds.bootstrapPassword
                            : "••••••"}
                        </code>
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className="text-mainSecond/60 hover:text-main shrink-0"
                          title={showPassword ? "Gizle" : "Göster"}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            handleCopy(
                              "bootstrapPassword",
                              revealedCreds.bootstrapPassword
                            )
                          }
                          className="text-mainSecond/60 hover:text-main shrink-0"
                          title="Kopyala"
                        >
                          {copiedField === "bootstrapPassword" ? (
                            <FaCheckCircle className="text-emerald-600" />
                          ) : (
                            <FaCopy />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="text-[11px] text-mainSecond/70 leading-relaxed">
                      Cihaz setup ekranında logo'ya basılı tutunca açılan
                      modal'a bu kodu girersen yeni kurulum ya da ekran
                      değişimi işlemine geçebilirsin. Cihaz başına benzersizdir.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Pending Details */}
        {pending && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-mainSecond/60 mb-1.5 font-semibold flex items-center gap-1.5">
              <FaKey className="w-3 h-3" />
              Kurulum Bilgisi
            </div>
            <div className="rounded-lg border border-input bg-white px-3 py-2 divide-y divide-input/60">
              <InfoRow
                label="Durum"
                value={PENDING_STATUS_LABEL[pending.status] || pending.status}
              />
              <InfoRow label="Mod" value={modeMeta?.label || pending.mode} />
              <InfoRow
                label="Hedef Seri No"
                value={pending.claimedSerialNo}
                mono
              />
              <InfoRow
                label="Atanan Seri No"
                value={pending.assignedSerialNo}
                mono
              />
              <InfoRow label="IMEI" value={pending.imei} mono />
              <InfoRow label="GSM No" value={pending.gsm} mono />
              <InfoRow label="Operatör" value={pending.carrier} />
              <InfoRow label="İşletim Sistemi" value={pending.osVersion} />
              <InfoRow label="Arayüz Sürümü" value={pending.uiVersion} />
              <InfoRow
                label="Donanım (Çay/Filtre)"
                value={pending.hardwareVersionTeaFilterCoffee}
              />
              <InfoRow
                label="Donanım (Türk Kahvesi)"
                value={pending.hardwareVersionTurkishCoffee}
              />
            </div>
          </div>
        )}

        {/* Action Panel */}
        <div className="border-t border-input pt-4">
          <div className="text-[11px] uppercase tracking-wider text-mainSecond/60 mb-2 font-semibold">
            Aksiyon
          </div>
          {renderActionPanel()}
        </div>
      </Drawer.Body>
    </Drawer>
  );
}
